import React from 'react';
import { Box, IconButton, Typography, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * Predefined set of vibe check emoji reactions.
 */
export const VIBE_REACTIONS = ['🔥', '💯', '👑', '💀', '🤮'] as const;
export type VibeReaction = (typeof VIBE_REACTIONS)[number];

export interface ReactionCounts {
  [emoji: string]: number;
}

export interface VibeCheckBarProps {
  outfitId: string;
  reactions: ReactionCounts;
  userReaction?: string | null;
  onReact: (emoji: string) => void;
  disabled?: boolean;
}

/** Human-readable labels for each emoji, used in aria-label attributes. */
const EMOJI_LABELS: Record<string, string> = {
  '🔥': 'fire',
  '💯': 'hundred points',
  '👑': 'crown',
  '💀': 'skull',
  '🤮': 'nauseated',
};

/**
 * VibeCheckBar — a horizontal row of emoji reaction buttons with aggregate counts.
 *
 * - Renders the predefined VIBE_REACTIONS set as tappable buttons.
 * - Shows aggregate count next to each emoji.
 * - Highlights the user's previous reaction with a primary-color ring.
 * - Plays a CSS scale animation on tap (gated behind prefers-reduced-motion).
 * - Disables reactions for unauthenticated users with a "Sign in to react" tooltip.
 * - Each button meets the 44×44 px minimum touch target on mobile.
 */
const VibeCheckBar: React.FC<VibeCheckBarProps> = ({
  outfitId,
  reactions,
  userReaction,
  onReact,
  disabled = false,
}) => {
  const theme = useTheme();

  return (
    <Box
      data-testid="vibe-check-bar"
      data-outfit-id={outfitId}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 0.5,
        flexWrap: 'wrap',
        /* CSS keyframes for the tap scale animation */
        '@keyframes vibePopScale': {
          '0%': { transform: 'scale(1.0)' },
          '50%': { transform: 'scale(1.4)' },
          '100%': { transform: 'scale(1.0)' },
        },
      }}
    >
      {VIBE_REACTIONS.map((emoji) => {
        const count = reactions[emoji] ?? 0;
        const isSelected = userReaction === emoji;
        const label = EMOJI_LABELS[emoji] ?? emoji;
        const ariaLabel = `React with ${label} emoji, ${count} reaction${count !== 1 ? 's' : ''}`;

        const button = (
          <IconButton
            key={emoji}
            data-testid={`vibe-btn-${emoji}`}
            aria-label={ariaLabel}
            disabled={disabled}
            onClick={() => onReact(emoji)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              /* 44px minimum touch target */
              minWidth: 44,
              minHeight: 44,
              borderRadius: '50%',
              padding: '4px',
              /* Highlight ring for the user's previous reaction */
              outline: isSelected
                ? `2px solid ${theme.palette.primary.main}`
                : '2px solid transparent',
              outlineOffset: '2px',
              transition: 'outline-color 150ms ease',
              /* Tap animation — gated behind prefers-reduced-motion */
              '@media (prefers-reduced-motion: no-preference)': {
                '&:active': {
                  animation: 'vibePopScale 200ms ease-in-out',
                },
              },
              /* Disable animation for users who prefer reduced motion */
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none !important',
                transition: 'none !important',
              },
            }}
          >
            <Typography
              component="span"
              sx={{ fontSize: '1.4rem', lineHeight: 1, userSelect: 'none' }}
            >
              {emoji}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.65rem', lineHeight: 1.2, mt: '2px' }}
            >
              {count}
            </Typography>
          </IconButton>
        );

        /* Wrap disabled buttons in a tooltip explaining why */
        if (disabled) {
          return (
            <Tooltip key={emoji} title="Sign in to react" arrow>
              {/* Tooltip needs a non-disabled child to show on hover, so wrap in a span */}
              <span>{button}</span>
            </Tooltip>
          );
        }

        return button;
      })}
    </Box>
  );
};

export default VibeCheckBar;
